import { ReactRenderer } from '@tiptap/react';
import {
  SuggestionKeyDownProps,
  SuggestionOptions,
  SuggestionProps,
} from '@tiptap/suggestion';
import tippy, { Instance } from 'tippy.js';

import { WorkerAttr } from '@/types/worker-attr';

import { WorkerAttrSuggestionList } from '../components/worker-attribute/worker-attr-suggestion-list';
import { WORKER_ATTR_NAME } from './constants';

import type { Editor } from '@tiptap/core';
import type { EditorState } from 'prosemirror-state';

type SuggestionTypes = WorkerAttr[];
type RefType = { onKeyDown: (props: SuggestionKeyDownProps) => boolean };

// Type aliases for TipTap suggestion types with proper typing
// The library types are incomplete, so we create properly typed versions
type TypedSuggestionProps = SuggestionProps<WorkerAttr, WorkerAttr> & {
  editor: Editor;
  clientRect?: (() => DOMRect) | null;
};

type TypedSuggestionKeyDownProps = SuggestionKeyDownProps & {
  event: KeyboardEvent;
};

// Tippy instance interface for methods we use
interface TippyInstance {
  hide: () => void;
  destroy: () => void;
  state: {
    isDestroyed: boolean;
  };
  setProps: (props: { getReferenceClientRect: () => DOMRect }) => void;
}

// ReactRenderer props interface
interface ReactRendererProps {
  props: TypedSuggestionProps;
  editor: Editor;
}

export function getSuggestionOptions(attributes: SuggestionTypes) {
  return {
    char: '{',
    allowSpaces: true,
    items: ({ query }: { query: string }) => {
      const sanitizedQuery = query.trimStart();
      // If query is empty (just typed '{'), return all attributes
      if (sanitizedQuery.length === 0) {
        return attributes;
      }
      return attributes.filter(item =>
        item.label.toLowerCase().includes(sanitizedQuery.toLowerCase()),
      );
    },
    command: ({
      editor,
      range,
      props,
    }: {
      editor: Editor;
      range: { from: number; to: number };
      props: WorkerAttr;
    }) => {
      const { nodeAfter } = editor.view.state.selection.$to;
      const overrideSpace = nodeAfter?.text?.startsWith(' ');
      const adjustedRange = overrideSpace
        ? { from: range.from, to: range.to + 1 }
        : range;

      const attrs = {
        id: props.id ?? '',
        label: props.label ?? '',
        fallback: props.fallback ?? '',
        tag: props.tag ?? 'talent',
        dataType: props.dataType ?? 'string',
        attrType: props.attrType,
      };

      editor
        .chain()
        .focus()
        .insertContentAt(adjustedRange, [
          {
            type: WORKER_ATTR_NAME,
            attrs,
          },
          {
            type: 'text',
            text: ' ',
          },
        ])
        .run();

      window.getSelection()?.collapseToEnd();
    },
    allow: ({
      state,
      range,
    }: {
      state: EditorState;
      range: { from: number; to: number };
    }) => {
      const $from = state.doc.resolve(range.from);
      const type = state.schema.nodes[WORKER_ATTR_NAME];
      if (!type) {
        return false;
      }
      const allow = !!$from.parent.type.contentMatch.matchType(type);
      return allow;
    },
    render: () => {
      let component: ReactRenderer<RefType, TypedSuggestionProps>;
      let popup: Instance[];

      return {
        onStart: (props: SuggestionProps<WorkerAttr, WorkerAttr>) => {
          const typedProps = props as unknown as TypedSuggestionProps;
          const rendererProps: ReactRendererProps = {
            props: typedProps,
            editor: typedProps.editor,
          };
          // ReactRenderer expects SuggestionProps but we're using TypedSuggestionProps
          // The types are compatible, so we use a type assertion
          component = new ReactRenderer<RefType, TypedSuggestionProps>(
            WorkerAttrSuggestionList as unknown as React.ComponentType<
              TypedSuggestionProps & { ref?: React.Ref<RefType> }
            >,
            rendererProps,
          );

          if (!typedProps.clientRect) {
            return;
          }

          const clientRectFn = typedProps.clientRect;
          popup = tippy('body', {
            getReferenceClientRect: () => clientRectFn() ?? new DOMRect(),
            appendTo: () => document.body,
            content: component.element,
            showOnCreate: true,
            interactive: true,
            interactiveDebounce: 0,
            interactiveBorder: 0,
            trigger: 'manual',
            placement: 'bottom-start',
            arrow: false,
            offset: [0, 8],
            hideOnClick: false,
            onClickOutside: (instance: Instance, event: MouseEvent) => {
              const target = event.target as HTMLElement;
              if (
                target.closest('[data-suggestion-list]') ||
                target.closest('input[data-search-input="true"]')
              ) {
                return false;
              }
              return true;
            },
            popperOptions: {
              modifiers: [
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                  },
                },
              ],
            },
          }) as unknown as Instance[];
        },
        onUpdate: (props: SuggestionProps<WorkerAttr, WorkerAttr>) => {
          if (component) {
            const typedProps = props as unknown as TypedSuggestionProps;
            component.updateProps(typedProps);
          }

          const typedProps = props as unknown as TypedSuggestionProps;
          if (!typedProps.clientRect || !popup) {
            return;
          }

          const tippyInstance = popup[0] as unknown as TippyInstance;
          if (!tippyInstance.state.isDestroyed) {
            tippyInstance.setProps({
              getReferenceClientRect: typedProps.clientRect,
            });
          }
        },
        onKeyDown: (props: SuggestionKeyDownProps) => {
          const typedProps = props as unknown as TypedSuggestionKeyDownProps;
          const keyboardEvent = typedProps.event;
          const target = keyboardEvent.target as HTMLElement;
          const { activeElement } = document;
          const isSearchInput =
            target?.tagName === 'INPUT' ||
            target?.tagName === 'TEXTAREA' ||
            target?.getAttribute('data-search-input') === 'true' ||
            !!target?.closest('input[data-search-input="true"]') ||
            activeElement?.tagName === 'INPUT' ||
            activeElement?.getAttribute('data-search-input') === 'true';

          if (isSearchInput) {
            if (keyboardEvent.key === 'Escape') {
              const tippyInstance = popup?.[0] as unknown as
                | TippyInstance
                | undefined;
              tippyInstance?.hide();
              return true;
            }
            keyboardEvent.preventDefault();
            keyboardEvent.stopPropagation();
            return true;
          }

          if (keyboardEvent.key === 'Escape') {
            const tippyInstance = popup?.[0] as unknown as
              | TippyInstance
              | undefined;
            tippyInstance?.hide();
            return true;
          }
          return component?.ref?.onKeyDown(props) ?? false;
        },
        onExit: () => {
          if (popup) {
            const tippyInstance = popup[0] as unknown as TippyInstance;
            tippyInstance.destroy();
          }
          if (component) {
            component.destroy();
          }
        },
      };
    },
  } as Omit<SuggestionOptions<unknown, WorkerAttr>, 'editor'>;
}
